import { useEffect, useRef, useState } from "react";

import { useResolveProspectFromLinkedinUrlMutation } from "@/pages/Private/redux/list/list.api";
import { ResolvedLinkedinProspect } from "@/pages/Private/pages/List/schema/list";

export type LinkedinResolutionStatus = "loading" | "resolved" | "failed";

export interface LinkedinResolution {
	url: string;
	status: LinkedinResolutionStatus;
	prospect?: ResolvedLinkedinProspect;
	reason?: string;
}

export type LinkedinResolutions = Record<string, LinkedinResolution>;

// Pull a human-readable reason out of an RTK Query error (the endpoint returns 400
// with a message body on failure); fall back to undefined so the UI shows its default.
const reasonOf = (err: unknown): string | undefined => {
	const data = (err as { data?: unknown })?.data;

	if (typeof data === "string") {
		return data;
	}

	const message = (data as { message?: unknown })?.message;

	return typeof message === "string" ? message : undefined;
};

// Resolves each pasted LinkedIn profile URL into a saved prospect via
// POST v1/list/person/from-linkedin-url, one request per URL. Results are keyed by the
// canonical URL so re-renders don't re-fire in-flight requests; URLs removed from the
// input are dropped from the map. A rejected request (e.g. 400) lands as `failed`.
export const useResolveLinkedinUrls = (validUrls: string[]): LinkedinResolutions => {
	const [resolveProspect] = useResolveProspectFromLinkedinUrlMutation();
	const [resolutions, setResolutions] = useState<LinkedinResolutions>({});
	// URLs we've already started resolving — prevents duplicate requests across renders.
	const startedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const present = new Set(validUrls);

		// Drop any url that's no longer in the input (chip removed).
		startedRef.current.forEach((url) => {
			if (!present.has(url)) {
				startedRef.current.delete(url);
			}
		});

		setResolutions((prev) => {
			const next: LinkedinResolutions = {};

			validUrls.forEach((url) => {
				if (prev[url]) {
					next[url] = prev[url];
				}
			});

			// Pruned at least one stale entry, or nothing to keep.
			const changed = Object.keys(next).length !== Object.keys(prev).length;

			return changed ? next : prev;
		});

		validUrls.forEach((url) => {
			if (startedRef.current.has(url)) {
				return;
			}

			startedRef.current.add(url);
			setResolutions((prev) => ({ ...prev, [url]: { url, status: "loading" } }));

			resolveProspect({ linkedinUrl: url })
				.unwrap()
				.then((prospect) =>
					setResolutions((prev) => ({ ...prev, [url]: { url, status: "resolved", prospect } }))
				)
				.catch((err) =>
					setResolutions((prev) => ({
						...prev,
						[url]: { url, status: "failed", reason: reasonOf(err) },
					}))
				);
		});
	}, [validUrls, resolveProspect]);

	return resolutions;
};
